import { palette } from 'constants/theme'

// `PaletteColorOptions` doesn't type its 0-950 shade keys as indexable (the same
// gap `constants/statuses.ts` has), so a narrow cast here keeps this file at zero
// added errors without loosening the shared palette type for every consumer.
const p = palette as unknown as Record<string, Record<number, string>>

/**
 * VRG-311 AC4 — "the list also surfaces age and never-opened state ... making
 * accumulation VISIBLE is the only thing standing between manual purge and nobody
 * ever purging." There is no automatic expiry (VRG-305 decision 9), so this status
 * is the whole mitigation — it has to be readable at a glance, not buried in a
 * count column.
 *
 * Mirrors the `CERTIFICATE_STATUSES` / `INVOICE_STATUSES` shape (constants/statuses)
 * so it plugs straight into the shared `StatusChipCell` + `statusFilterOptions`.
 */
export const DEMO_ENGAGEMENT_STATUSES = {
  purged: {
    label: 'Purged',
    color: p.gray[700],
    borderColor: p.gray[200],
    backgroundColor: p.gray[100],
  },
  /**
   * VRG-361 Slice 2 — the demo closed. `demo_taken_over_at` is stamped, so this
   * company is a paying customer and the row survives only as history (AC1: both
   * facts true at once).
   *
   * It ranks ABOVE opened/never-opened rather than sitting beside them because it
   * is a different kind of statement: engagement is about the prospect's
   * behaviour, and this is about the outcome. A converted demo was necessarily
   * opened, so showing "Opened (3)" there would report the less important of two
   * true things — and it is also what makes "which demos actually closed" a
   * one-click filter rather than a question nothing on this page can answer.
   */
  converted: {
    label: 'Converted',
    color: p.blue[700],
    borderColor: p.blue[200],
    backgroundColor: p.blue[100],
  },
  never_opened: {
    label: 'Never opened',
    color: p.orange[700],
    borderColor: p.orange[200],
    backgroundColor: p.orange[100],
  },
  opened: {
    label: 'Opened',
    color: p.green[700],
    borderColor: p.green[200],
    backgroundColor: p.green[100],
  },
} as const

export type DemoEngagementStatus = keyof typeof DEMO_ENGAGEMENT_STATUSES

export const demoEngagementStatus = (demo: {
  isPurged: boolean
  neverOpened: boolean
  demoTakenOverAt?: string | null
}): DemoEngagementStatus => {
  // Purged first: the company row is gone, so nothing else about it is true any
  // more. Converted second — see the note on the `converted` entry above.
  if (demo.isPurged) return 'purged'
  if (demo.demoTakenOverAt) return 'converted'
  if (demo.neverOpened) return 'never_opened'
  return 'opened'
}

/**
 * VRG-361 — the invited admin's CONSOLE TRIAL, a DIFFERENT clock from
 * `demoEngagementStatus`/`demoExpiresAt` above (the public certificate's
 * lifetime). Shared by the "Trial" column and `ExtendTrialModal`'s "Currently"
 * line so the two never describe the same state differently.
 *
 * `not_started` covers BOTH "no admin has accepted yet" and "an AE pre-set a
 * future trialEndsAt before that happened" (ExtendTrialUseCase explicitly
 * supports the latter) — trialStartedAt, not trialEndsAt, is what actually
 * started the clock.
 */
export type DemoTrialStatus = 'not_started' | 'active' | 'expired'

export const demoTrialStatus = (demo: {
  trialStartedAt: string | null
  trialEndsAt: string | null
}): DemoTrialStatus => {
  if (!demo.trialStartedAt) return 'not_started'
  if (demo.trialEndsAt && new Date(demo.trialEndsAt).getTime() <= Date.now()) return 'expired'
  return 'active'
}

// Lifetime/tier constants + STALE_DEMO_AGE_DAYS live in `constants/demos` — shared
// with CreateDemoModal (components/-layer), which must not reach into pages/-layer.
export { DEMO_TIER_LABELS, DEMO_LIFETIME_OPTIONS, STALE_DEMO_AGE_DAYS, lifetimeToExpiresAt } from 'constants/demos'
export type { DemoLifetimeValue } from 'constants/demos'
